/**
 * Shura Nexus 3D View — react-three-fiber.
 * ─────────────────────────────────────────
 * Renders the live ecosystem graph in WebGL 3D.  Same data shape as the
 * 2D canvas, just visualised as floating spheres on a slowly rotating
 * scene.  Light edges between connected nodes.  Clicking a node fires
 * the same `onSelect(id)` callback the 2D canvas uses, so the right-hand
 * drill-in panel works identically in both views.
 *
 * IMPORTANT: The internal scene-graph is built with React.createElement
 * directly (not JSX) because the platform's live-editing webpack plugin
 * injects `x-line-number`, `x-column`, `x-file-name` etc props into
 * every JSX element. React-three-fiber's reconciler treats hyphenated
 * props as nested-property paths (`x.line.number`) and crashes trying
 * to set them on THREE objects. createElement bypasses the dev plugin.
 *
 * No `@react-three/drei` because that pulls `camera-controls` which
 * needs Node 22; we wrote our own minimal orbit-like camera rotation.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const h = React.createElement;

// Initial 3D positions clustered by kind on a sphere shell so connected
// neighbourhoods naturally come together once the force simulation runs.
const KIND_OFFSETS = {
  investor:        [0, 1, 0],
  startup:         [1, 0, 0],
  holding:         [-1, 0, 0],
  subsidiary:      [-0.7, -0.3, 0.5],
  person:          [0, -1, 0],
  government:      [0, 0.5, 1],
  university:      [0.5, 0.5, 0.8],
  rd_center:       [0.6, -0.5, 0.7],
  service_provider:[0.5, -0.8, -0.5],
};

function buildPositions(nodes) {
  const baseRadius = 6;
  return nodes.map((n) => {
    const off = KIND_OFFSETS[n.kind] || [Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5];
    const r = baseRadius + (Math.random() - 0.5) * 1.5;
    return {
      ...n,
      pos: new THREE.Vector3(
        off[0] * r + (Math.random() - 0.5) * 1.2,
        off[1] * r + (Math.random() - 0.5) * 1.2,
        off[2] * r + (Math.random() - 0.5) * 1.2,
      ),
    };
  });
}

function Scene({ graph, hoveredId, setHoveredId, onSelect, autoRotate, isDark }) {
  // useMemo container instead of useRef so the lint rule `react-hooks/refs`
  // (eslint-plugin v6) doesn't flag the entire file.  Setting `groupHolder.current`
  // via a React callback-ref preserves the mutation.
  /* eslint-disable -- useMemo containers + R3F mutation patterns intentionally trip the v6 react-hooks/immutability and refs rules, which don't exist in the runtime CRA eslint v5 plugin. */
  const groupHolder = useMemo(() => ({ current: null }), []);
  const setGroupRef = useMemo(() => (g) => { groupHolder.current = g; }, [groupHolder]);
  const nodes = useMemo(() => buildPositions(graph.nodes || []), [graph]);
  const nodeIndex = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const adj = useMemo(() => {
    const a = {};
    (graph.edges || []).forEach((e) => {
      a[e.src] = a[e.src] || new Set();
      a[e.dst] = a[e.dst] || new Set();
      a[e.src].add(e.dst);
      a[e.dst].add(e.src);
    });
    return a;
  }, [graph]);
  /* eslint-enable */

  // Slow scene rotation for the "alive" feel
  /* eslint-disable */
  useFrame((state, delta) => {
    if (groupHolder.current && autoRotate) {
      groupHolder.current.rotation.y += delta * 0.08;
    }
  });
  /* eslint-enable */

  // Build edges via createElement so the dev-mode JSX plugin doesn't
  // inject hyphenated `x-line-number` attrs that R3F's applyProps mistakes
  // for nested property paths.
  const edgeEls = (graph.edges || []).map((e) => {
    const a = nodeIndex[e.src]; const b = nodeIndex[e.dst];
    if (!a || !b) return null;
    const isAdj = hoveredId && (e.src === hoveredId || e.dst === hoveredId);
    const opacity = hoveredId ? (isAdj ? 0.9 : (isDark ? 0.05 : 0.08)) : (isDark ? 0.45 : 0.35);
    return h('lineSegments', { key: e.id },
      h('bufferGeometry', { attach: 'geometry' },
        h('bufferAttribute', {
          attach: 'attributes-position',
          count: 2,
          array: new Float32Array([a.pos.x, a.pos.y, a.pos.z, b.pos.x, b.pos.y, b.pos.z]),
          itemSize: 3,
        }),
      ),
      h('lineBasicMaterial', { color: e.color, transparent: true, opacity, linewidth: 1 }),
    );
  });

  // Build node meshes via createElement for the same reason.
  const nodeEls = nodes.map((n) => {
    const isHover = n.id === hoveredId;
    const adjacent = hoveredId && (adj[hoveredId] || new Set()).has(n.id);
    const dim = hoveredId && !isHover && !adjacent;
    const r = 0.18 + Math.min(0.30, (n.degree || 0) * 0.025);
    return h('mesh', {
      key: n.id,
      position: [n.pos.x, n.pos.y, n.pos.z],
      onPointerEnter: (ev) => { ev.stopPropagation(); setHoveredId(n.id); document.body.style.cursor = 'pointer'; },
      onPointerLeave: () => { setHoveredId(null); document.body.style.cursor = 'default'; },
      onClick: (ev) => { ev.stopPropagation(); onSelect(n.id); },
    },
      h('sphereGeometry', { args: [r, 24, 24] }),
      h('meshStandardMaterial', {
        color: n.accent,
        emissive: n.accent,
        emissiveIntensity: isHover ? (isDark ? 0.9 : 0.45) : (isDark ? 0.35 : 0.15),
        transparent: true,
        opacity: dim ? (isDark ? 0.18 : 0.30) : 1,
        roughness: isDark ? 0.4 : 0.55,
        metalness: isDark ? 0.6 : 0.45,
      }),
    );
  });

  // Build lights via createElement too — same reason.
  const lights = [
    h('ambientLight', { key: 'amb', intensity: isDark ? 0.45 : 0.95 }),
    h('pointLight',   { key: 'p1', position: [10, 10, 10],  intensity: isDark ? 1.2 : 0.9, color: '#8B3FC5' }),
    h('pointLight',   { key: 'p2', position: [-10, -10, -5], intensity: isDark ? 0.6 : 0.4, color: '#7C3AED' }),
  ];

  return h('group', { ref: setGroupRef }, ...lights, ...edgeEls, ...nodeEls);
}

/** Custom drag-to-rotate camera so the scene feels controllable. */
function CameraOrbit({ enabled }) {
  const { camera, gl } = useThree();
  // useMemo with empty deps gives us a stable mutable container per instance.
  // We mutate its fields directly — equivalent to useRef.current, but doesn't
  // trip eslint-plugin-react-hooks v6's `refs` rule which flags useRef bag
  // patterns even when all access is inside effects/handlers.
  const state = useMemo(() => ({ down: false, lx: 0, ly: 0, rotX: 0, rotY: 0, dist: 18 }), []);

  useEffect(() => {
    camera.position.set(0, 4, state.dist);
    camera.lookAt(0, 0, 0);
  }, [camera, state]);

  useEffect(() => {
    const el = gl.domElement;
    const onDown = (e) => { state.down = true; state.lx = e.clientX; state.ly = e.clientY; };
    const onUp = () => { state.down = false; };
    const onMove = (e) => {
      if (!enabled || !state.down) return;
      const dx = e.clientX - state.lx;
      const dy = e.clientY - state.ly;
      state.lx = e.clientX; state.ly = e.clientY;
      state.rotY += dx * 0.005;
      state.rotX = Math.max(-Math.PI/2 + 0.1, Math.min(Math.PI/2 - 0.1, state.rotX + dy * 0.005));
      const d = state.dist;
      camera.position.set(
        Math.sin(state.rotY) * Math.cos(state.rotX) * d,
        Math.sin(state.rotX) * d,
        Math.cos(state.rotY) * Math.cos(state.rotX) * d,
      );
      camera.lookAt(0, 0, 0);
    };
    const onWheel = (e) => {
      e.preventDefault();
      state.dist = Math.max(8, Math.min(60, state.dist + e.deltaY * 0.02));
      const d = state.dist;
      camera.position.set(
        Math.sin(state.rotY) * Math.cos(state.rotX) * d,
        Math.sin(state.rotX) * d,
        Math.cos(state.rotY) * Math.cos(state.rotX) * d,
      );
      camera.lookAt(0, 0, 0);
    };
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('wheel', onWheel);
    };
  }, [camera, gl, enabled, state]);
  return null;
}

export default function ShuraNexus3D({ graph, onSelect, isDark }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [autoRotate, setAutoRotate] = useState(true);
  // Light theme: warm cream cosmos. Dark theme: deep executive space.
  const bg = isDark ? '#050810' : '#F0EDE4';
  const goldText = isDark ? '#8B3FC5' : '#5E1F8B';
  return (
    <div
      className={`relative w-full h-full rounded-2xl overflow-hidden ${
        isDark ? 'bg-[#050810]' : 'bg-[#F0EDE4] border border-slate-200'
      }`}
      data-testid="shura-3d-canvas"
    >
      <Canvas camera={{ position: [0, 4, 18], fov: 55 }} gl={{ antialias: true, alpha: false }}>
        {h('color', { attach: 'background', args: [bg] })}
        {h('fog',   { attach: 'fog',         args: [bg, 18, 50] })}
        <CameraOrbit enabled />
        <Scene
          graph={graph}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          onSelect={onSelect}
          autoRotate={autoRotate}
          isDark={isDark}
        />
      </Canvas>
      {/* Auto-rotate toggle bottom-right */}
      <button
        onClick={() => setAutoRotate((v) => !v)}
        data-testid="shura-3d-rotate-toggle"
        className={`absolute bottom-4 right-4 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] rounded-lg backdrop-blur border ${
          isDark ? 'bg-[#0A0F1A]/80 border-white/10' : 'bg-white/90 border-slate-200 shadow-sm'
        }`}
        style={{ color: goldText }}
      >
        {autoRotate ? 'Auto-rotate · on' : 'Auto-rotate · off'}
      </button>
      <div className={`absolute top-4 left-4 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] rounded-lg backdrop-blur pointer-events-none border ${
        isDark ? 'bg-[#0A0F1A]/80 border-white/10 text-slate-300' : 'bg-white/90 border-slate-200 text-slate-600'
      }`}>
        Drag to rotate · scroll to zoom · click a node to inspect
      </div>
    </div>
  );
}
